<template>
  <div class="editor-container">
    <div v-if="selectedElement" class="card">
      <cardHeader :elementLabel="elementLabel" :elementType="selectedElement.el_Type" :mode="'Editor'" />
      <div class="card-body">
        <form @submit.prevent="saveElement">
          <div class="mb-3">
            <h3>
              <input placeholder="Main prompt" type="text" class="inputMainPrompt" v-model="selectedElement.el_Text" />
            </h3>
          </div>
          <div class="form-group mb-3">
            <div ref="quillEditor" class="quill-editor"></div>
          </div>
        </form>
        You can create categories to retreive using the stickers. Type a word or use an <a href="https://emojipedia.org/" target="_blank">Emojis</a> 
        <br> like these : ✅ ❌ ❎ ⭕ 🔅 🔴 🔸🔻🚀 🛸 and much more...(just copy and paste!)
        <ZoneDataTable :zones="zones" :selectedZoneId="selectedZoneId" :stickerZone="stickerZone"
          @select-zone="handleZoneSelection" />
        <div class="mb-3">
          <div class="form-floating mb-3">
            <input type="number" class="form-control" id="floatingInput"
              v-model.number="selectedElement.el_data.penalties" placeholder="Penalties">
            <label for="floatingInput">Wrong click penalties</label>
          </div>
        </div>

        <div class="canvas-toolbar">
          <div class="btn-group mb-2">
            <button class="btn btn-outline-secondary btn-sm" :class="{ active: shapeMode === 'circle' }"
              @click="handleAddZone('circle')" title="Add Circle Zone" v-html="circleShaper">
            </button>

            <button class="btn btn-outline-secondary btn-sm" :class="{ active: shapeMode === 'rect' }"
              @click="handleAddZone('rect')" title="Add Rectangle Zone" v-html="squareShaper">
            </button>

            <button class="btn btn-outline-secondary btn-sm" :class="{ active: shapeMode === 'freeform' }"
              @click="startFreeform" title="Draw Freeform Zone" v-html="freeformLogo">
            </button>

            <button class="btn btn-outline-danger btn-sm btdel" @click="deleteSelectedZone" :disabled="!selectedZoneId"
              title="Delete Selected Zone">
              🗑️
            </button>

            <button class="BGloader" @click.prevent="triggerFileInput" :disabled="isProcessingImage">
              {{ isProcessingImage ? 'Processing...' : 'Load background image' }}
            </button>
            <input ref="fileInput" type="file" accept="image/*" style="display: none" @change="handleImageUpload" />

            <!-- Indicateur de taille et compression -->
            <div v-if="imageMetadata" class="image-info">
              <small class="text-muted">
                {{ imageMetadata.sizeFormatted }}
                <span v-if="imageMetadata.compressedSize">(compressed from {{ formatFileSize(imageMetadata.originalSize) }})</span>
              </small>
            </div>
          </div>
        </div>

        <div class="canvas-wrapper">
          <div ref="canvasRef" class="konva-container"></div>
        </div>
      </div>
            
      <!-- Slider de difficulté -->
      <DifficultySlider 
        v-model="selectedElement.difficulty_rating"
        @update:model-value="updateElement('difficulty_rating', $event)"
      />
      
      <dimension v-if="hasDimensions" />
      <div class="card-footer">
        <EditorActionBar :selectedElement="selectedElement" :elementLabel="elementLabel" @save-element="saveElement"
          :isItemBank="isItemBank" />
      </div>
    </div>
    <div v-else class="alert alert-info">
      Select an element to edit
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import Quill from 'quill'
import Konva from 'konva'
import { useTestStore } from '../../../stores/testStore'
import cardHeader from '../default/cardHeader.vue'
import EditorActionBar from '../default/EditorActionBar.vue'
import { useKonvaCanvas } from './composables/useKonvaCanvas'
import { useZoneManager } from './composables/useZoneManager'
import { ImageManager } from '../../../utils/imageUtils'
import freeformLogoSvg from '@/assets/graphicInteractions/freeform.svg?raw'
import circleShaperSvg from '@/assets/graphicInteractions/CircleShaper.svg?raw'
import squareShaperSvg from '@/assets/graphicInteractions/SquareShaper.svg?raw'
import ZoneDataTable from './ZoneDataTable.vue'
import { debounce } from 'lodash-es'
import dimension from '../default/dimension.vue';


import DifficultySlider from '../../common/DifficultySlider.vue';
const props = defineProps({
  isItemBank: { type: Boolean, default: false }
})

// Computed pour vérifier si les dimensions doivent être affichées
const hasDimensions = computed(() => {
  const selectedEl = store.selectedElement;
  const testData = store.testData;
  
  // Afficher si l'élément a déjà des dimensions assignées
  const hasAssignedDimensions = selectedEl?.dimension && selectedEl.dimension.length > 0;
  
  // Afficher si des dimensions sont disponibles pour être ajoutées
  const hasAvailableDimensions = testData?.dimensions && testData.dimensions.length > 0;
  
  return hasAssignedDimensions || hasAvailableDimensions;
});

const freeformLogo = ref(freeformLogoSvg)
const circleShaper = ref(circleShaperSvg)
const squareShaper = ref(squareShaperSvg)

const store = useTestStore()
const selectedElement = computed(() => store.selectedElement)
const elementLabel = ref('')
const canvasRef = ref(null)
const quillEditor = ref(null)
let quillInstance = null
const shapeMode = ref(null)
const selectedZoneId = ref(null)
const stickerZone = ref(null);
const zones = ref([])

// Protection contre les mises à jour répétitives
let isUpdatingElement = false;

// Utility function pour mettre à jour les éléments
const updateElement = (key, value) => {
  if (!props.isItemBank && !isUpdatingElement) {
    // Sauvegarde automatique uniquement dans le contexte du test
    console.log(`Updating element in test context: key=${key}, value=${value}`);
    if (selectedElement.value) {
      isUpdatingElement = true;
      const keys = key.split('.');
      let obj = selectedElement.value;
      while (keys.length > 1) obj = obj[keys.shift()];
      obj[keys[0]] = value;

      // Utiliser nextTick pour éviter les collisions
      nextTick(() => {
        store.updateElement(selectedElement.value);
        isUpdatingElement = false;
      });
    }
  } else {
    // Logique spécifique à l'Item Bank (si nécessaire)
    console.log("Item Bank context detected. No auto-save triggered.");
  }
};

// Nouvelles variables pour la gestion des images
const imageManager = new ImageManager()
const isProcessingImage = ref(false)
const imageMetadata = ref(null)

// ✅ Déclaration AVANT l'appel à useKonvaCanvas
const localSaveElement = () => {
  if (!selectedElement.value) return;

  // Sécurisation : s'assurer que zones.value est bien un tableau
  const zoneArray = Array.isArray(zones.value) ? zones.value : [];

  // Nettoyage des zones pour ne garder que les propriétés utiles
  const cleanZones = zoneArray.map(zone => ({
    ...zone,
    message: undefined, // on supprime le champ message s'il existe
    name: zone.name || '', // nom par défaut vide si absent
    score: zone.score ?? 0, // score 0 par défaut
  }));

  // Affectation au bon champ dans l'élément sélectionné
  selectedElement.value.el_Data = JSON.parse(JSON.stringify(cleanZones)); // deep clone pour éviter les effets de bord
  selectedElement.value.el_data.stickerZoneId = stickerZone.value?.id || null;

  // Mise à jour dans le store (Pinia) avec protection
  if (!isUpdatingElement) {
    isUpdatingElement = true;
    nextTick(() => {
      store.updateElement({ ...selectedElement.value });
      isUpdatingElement = false;
    });
  }
}

const saveElement = localSaveElement;

const {
  stage,
  zoneLayer,
  initStage,
  drawExistingZones,
  deleteSelectedZone,
  registerDrawZone,
  setBackgroundImage,
  isZoomLocked,
  toggleZoomLock,
  clearSelection
} = useKonvaCanvas(canvasRef, selectedElement, selectedZoneId, zones, localSaveElement)

const fileInput = ref(null)

function triggerFileInput() {
  if (fileInput.value) fileInput.value.click()
}

// Nouvelle fonction pour gérer l'upload avec conversion Base64
async function handleImageUpload(event) {
  const file = event.target.files[0];
  if (!file) return;

  isProcessingImage.value = true;
  imageMetadata.value = null;

  try {
    let result;
    
    // Essayer d'abord la conversion directe
    try {
      result = await imageManager.fileToBase64(file);
    } catch (error) {
      // Si le fichier est trop gros, essayer la compression
      console.log('Fichier trop volumineux, compression en cours...');
      result = await imageManager.resizeAndConvert(file);
    }

    const { base64, metadata } = result;
    imageMetadata.value = metadata;

    // Sauvegarder l'image Base64 dans l'élément
    if (!selectedElement.value.el_data) {
      selectedElement.value.el_data = {};
    }
    
    selectedElement.value.el_data.backgroundImage = {
      base64: base64,
      metadata: metadata,
      timestamp: Date.now()
    };

    // Utiliser l'image Base64 directement pour l'affichage
    setBackgroundImage(base64);

    // Sauvegarder l'élément
    saveElement();

    console.log('Image convertie et sauvegardée:', {
      size: metadata.sizeFormatted,
      dimensions: metadata.dimensions || `${metadata.originalDimensions?.width || '?'}x${metadata.originalDimensions?.height || '?'}`
    });

  } catch (error) {
    console.error("Erreur lors du traitement de l'image:", error);
    alert(`Erreur: ${error.message}`);
  } finally {
    isProcessingImage.value = false;
    // Réinitialiser l'input file
    if (fileInput.value) fileInput.value.value = '';
  }
}

// Fonction utilitaire pour formater la taille
function formatFileSize(bytes) {
  return imageManager.formatFileSize(bytes);
}

const {
  drawZone,
  drawNewZone,
  addZone,
  startFreeform
} = useZoneManager(stage, zoneLayer, selectedZoneId, localSaveElement, zones, stickerZone)

registerDrawZone(drawZone)

function handleAddZone(type) {
  shapeMode.value = type;
  canvasRef.value.style.cursor = 'crosshair';
}

function handleCanvasClick(event) {
  if (!shapeMode.value) return;

  const stageEl = stage.value;
  const pointer = stageEl?.getPointerPosition();
  if (!pointer) return;

  const newId = drawNewZone(shapeMode.value, pointer.x, pointer.y);
  if (!newId) return;

  handleZoneSelection(newId);
  localSaveElement();

  shapeMode.value = null;
  canvasRef.value.style.cursor = 'default';
}

function updateMaxScore() {
  const el = selectedElement.value;
  if (!el || !Array.isArray(el.el_Data)) {
    el.maxScore = 0;
    return;
  }
  const total = el.el_Data.reduce((sum, z) => sum + (Number(z.score) || 0), 0);
  el.maxScore = total;
}

function handleZoneSelection(zoneId) {
  const shape = zoneLayer.value.findOne(`#${zoneId}`)
  if (!shape) return
  selectedZoneId.value = zoneId
  clearSelection()
  zoneLayer.value.find('Transformer').forEach(tr => tr.destroy())

  const tr = new Konva.Transformer({
    node: shape,
    enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
    boundBoxFunc: (oldBox, newBox) => {
      return (newBox.width < 10 || newBox.height < 10) ? oldBox : newBox
    }
  })

  zoneLayer.value.add(tr)
  zoneLayer.value.draw()
}

const debouncedSave = debounce(() => {
  localSaveElement()
  updateMaxScore()
}, 500)

watch(zones, () => {
  updateMaxScore()
  debouncedSave()
}, { deep: true })

watch(() => store.selectedElementLabel, newLabel => {
  elementLabel.value = newLabel || ''
}, { immediate: true })

watch([selectedElement, stage, zoneLayer], ([el, stageReady, layerReady]) => {
  if (el?.el_Data && stageReady && layerReady) {
    zones.value = JSON.parse(JSON.stringify(el.el_Data));
    if (Array.isArray(el?.el_Data)) {
      zones.value = JSON.parse(JSON.stringify(el.el_Data));
      drawExistingZones(zones.value);
    } else {
      zones.value = [];
    }
    stickerZone.value = el.el_data?.stickerZoneId
      ? zones.value.find(z => z.id === el.el_data.stickerZoneId)
      : null;
    
    // Charger l'image de fond si elle existe
    if (el.el_data?.backgroundImage?.base64) {
      setBackgroundImage(el.el_data.backgroundImage.base64);
      imageMetadata.value = el.el_data.backgroundImage.metadata;
    }
  }
}, { immediate: true });

watch(selectedElement, el => {
  if (el && !el.el_data) el.el_data = {};
}, { immediate: true })

function handleKeyDown(event) {
  if (event.key === 'Delete' && selectedZoneId.value) {
    deleteSelectedZone()
  }
}

onMounted(async () => {
  canvasRef.value.addEventListener('click', handleCanvasClick);

  if (quillEditor.value) {
    quillInstance = new Quill(quillEditor.value, {
      theme: 'snow',
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          [{ header: 1 }, { header: 2 }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          [{ color: [] }, { background: [] }],
          ['image', 'link'],
          ['clean']
        ]
      }
    })
    if (selectedElement.value?.el_RichText) {
      quillInstance.root.innerHTML = selectedElement.value.el_RichText
    }
    // Protection contre les boucles infinies
    let isUpdatingQuill = false;
    
    quillInstance.on('text-change', (delta, oldDelta, source) => {
      // Ignorer les changements programmatiques pour éviter les boucles
      if (source === 'api' || isUpdatingQuill) return;

      if (selectedElement.value) {
        isUpdatingQuill = true;
        selectedElement.value.el_RichText = quillInstance.root.innerHTML
        if (!props.isItemBank) {
          // Utiliser nextTick pour éviter les collisions de mises à jour
          nextTick(() => {
            store.updateElement({ ...selectedElement.value })
            isUpdatingQuill = false;
          });
        } else {
          isUpdatingQuill = false;
        }
      }
    })
  }

  window.addEventListener('keydown', handleKeyDown)

  // Attendre que le DOM soit prêt avant d'initialiser
  await nextTick()
  
  // IMPORTANT: Initialiser les données AVANT d'appeler initStage()
  if (!selectedElement.value.el_data) selectedElement.value.el_data = {}
  selectedElement.value.el_data.penalties ??= 0

  if (selectedElement.value?.el_Data) {
    zones.value = JSON.parse(JSON.stringify(selectedElement.value.el_Data))
  }

  if (selectedElement.value.el_data?.penalties !== undefined) {
    selectedElement.value.el_Penalties = selectedElement.value.el_data.penalties;
  }

  // Charger l'image de fond existante dans el_data
  if (selectedElement.value.el_data?.backgroundImage?.base64) {
    imageMetadata.value = selectedElement.value.el_data.backgroundImage.metadata;
  }

  // MAINTENANT initialiser le stage avec les données prêtes
  if (canvasRef.value) {
    await initStage()
    
    // Dessiner les zones existantes après l'initialisation du stage
    if (selectedElement.value?.el_Data && Array.isArray(selectedElement.value.el_Data)) {
      drawExistingZones(zones.value)
    }
  } else {
    console.warn('Canvas container not ready during mount, delaying stage creation')
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
  canvasRef.value?.removeEventListener('click', handleCanvasClick);
})
</script>

<style scoped>
.canvas-toolbar .btn.active {
  background-color: #0d6efd;
  color: white;
}

.canvas-toolbar svg {
  margin-right: 4px;
}

.card-header {
  background-color: #426C9C;
  color: white;
}

.inputMainPrompt {
  width: 100%;
  padding: 8px;
  font-size: 16px;
}

.quill-editor {
  min-height: 150px;
  border: 1px solid #ccc;
  padding: 10px;
}

.canvas-wrapper {
  width: 100%;
  border: 1px dashed #999;
  margin-top: 10px;
  overflow: auto;
}

.konva-container {
  display: inline-block;
}

.BGloader {
  background-color: white;
  border: 2px solid #ccc;
  color: black;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
}

.BGloader:hover:not(:disabled) {
  background-color: #a8bcf5;
  border-color: #3b4083;
  color: white;
}

.BGloader:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.canvas-toolbar button svg {
  width: 30px;
  height: 30px;
  display: inline-block;
  vertical-align: middle;
}

.zoomer {
  top: 0px;
  right: 0px;
  z-index: 0;
  font-size: 30px;
}

.zoomLocker {
  position: absolute;
  top: 5px;
  right: 30px;
  z-index: 10;
}

.image-info {
  margin-left: 10px;
  display: flex;
  align-items: center;
}
</style>